import { Component, inject, input, output, signal, viewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { lastValueFrom, map } from 'rxjs';
import {
  getCreateAbsenceV2DtoFormGroup,
  getUpdateAbsenceV2DtoFormGroup,
  WorkAbsence,
  WorkAbsenceDayPartEnum,
  WorkAbsencesV2Service,
  WorkCreateAbsenceV2Dto,
  WorkUpdateAbsenceV2Dto,
} from '@work-frontend-monorepo/work-rest-api';
import { AlertDialogService, ModalComponent } from '@work-frontend-monorepo/work-ui/modals';
import {
  InputDatepickerComponent,
  InputFieldComponent,
  SelectComponent,
  SelectDropdownOptions,
  SelectDropdownOptionsApiService,
} from '@work-frontend-monorepo/work-ui/forms';
import { ButtonComponent } from '@work-frontend-monorepo/work-ui/layout';
import { ExceptionHandlingService } from '@work-frontend-monorepo/work-ui/services';
import { LoadingAndFeedbackState } from '@work-frontend-monorepo/work-ui/utilities';

@Component({
  selector: 'sfg-app-absenzen-create-update-modal',
  templateUrl: './absenzen-create-update-modal.component.html',
  styleUrls: ['./absenzen-create-update-modal.component.scss'],
  standalone: true,
  imports: [
    ModalComponent,
    ReactiveFormsModule,
    SelectComponent,
    InputDatepickerComponent,
    InputFieldComponent,
    ButtonComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class AbsenzenCreateUpdateModalComponent {
  userId = input.required<number>();
  absenceEdited = output<void>();

  modal = viewChild<ModalComponent>('absenceCreateUpdateModal');

  absenceToUpdate = signal<WorkAbsence | undefined>(undefined);
  formGroupCreate = getCreateAbsenceV2DtoFormGroup();
  formGroupUpdate = getUpdateAbsenceV2DtoFormGroup();
  formSaveLoadingState: LoadingAndFeedbackState = 'none';

  private readonly workAbsencesV2Service = inject(WorkAbsencesV2Service);
  private readonly selectDropdownOptionsService = inject(SelectDropdownOptionsApiService);
  private readonly exceptionHandlingService = inject(ExceptionHandlingService);
  private readonly alertDialogService = inject(AlertDialogService);

  // Filter timerecordtypes to only show absence-relevant types: Ferien, Militär/Zivilschutz
  dropdownOptionsTimeRecordType$ = this.selectDropdownOptionsService.timerecordtypes$.pipe(
    map((types) => types.filter((t) => ['Ferien', 'Militär, Zivilschutz'].includes(t.label))),
  );

  readonly dayPartOptions: SelectDropdownOptions[] = [
    { value: WorkAbsenceDayPartEnum.Full, label: 'GANZER_TAG' },
    { value: WorkAbsenceDayPartEnum.Morning, label: 'VORMITTAG' },
    { value: WorkAbsenceDayPartEnum.Afternoon, label: 'NACHMITTAG' },
  ];

  openModal(absence?: WorkAbsence) {
    this.resetForm();
    this.absenceToUpdate.set(absence);
    if (absence) {
      this.formGroupUpdate.patchValue(absence);
    } else {
      this.formGroupCreate.patchValue({ createdBy: this.userId() });
    }
    this.modal()?.open();
  }

  protected async createAbsence() {
    try {
      this.formSaveLoadingState = 'loading';
      await lastValueFrom(
        this.workAbsencesV2Service.absencesV2ControllerCreate({
          body: this.formGroupCreate.value as WorkCreateAbsenceV2Dto,
        }),
      );
      this.formSaveLoadingState = 'success';
      this.absenceEdited.emit();
      this.alertDialogService.showAlert({ title: 'ERSTELLEN_ERFOLGREICH', feedbackState: 'success' });
      this.modal()?.close();
    } catch (createAbsenceException) {
      this.formSaveLoadingState = 'error';
      this.exceptionHandlingService.handleExceptionWithAlert('createAbsenceException', createAbsenceException);
    }
  }

  protected async updateAbsence() {
    const absence = this.absenceToUpdate();
    if (!absence) return;
    try {
      this.formSaveLoadingState = 'loading';
      await lastValueFrom(
        this.workAbsencesV2Service.absencesV2ControllerUpdate({
          id: absence.id,
          body: this.formGroupUpdate.value as WorkUpdateAbsenceV2Dto,
        }),
      );
      this.formSaveLoadingState = 'success';
      this.absenceEdited.emit();
      this.modal()?.close();
    } catch (updateAbsenceException) {
      this.formSaveLoadingState = 'error';
      this.exceptionHandlingService.handleExceptionWithAlert('updateAbsenceException', updateAbsenceException);
    }
  }

  private resetForm() {
    this.formGroupCreate = getCreateAbsenceV2DtoFormGroup();
    this.formGroupUpdate = getUpdateAbsenceV2DtoFormGroup();
    this.absenceToUpdate.set(undefined);
    this.formSaveLoadingState = 'none';
  }
}
